/**
 * Shared helpers for rendering Stripe-backed activation plan pricing across frontend billing surfaces.
 */
import { StripeActivationPlanPricingDetails } from '../interfaces/BillingTypes';

import { AbacusCurrency } from './abacusCurrency';

export function formatActivationPlanAmount(amountInCents: number | null | undefined, currencyCode = 'USD') {
  if (amountInCents == null) {
    return null;
  }

  return new AbacusCurrency(amountInCents, currencyCode).toFormattedString(true);
}

export function isFreeActivationPlan(pricingDetails: StripeActivationPlanPricingDetails | null | undefined) {
  if (!pricingDetails) {
    return false;
  }

  return (pricingDetails.additionalUserAmountInCents ?? 0) === 0 && (pricingDetails.minimumMonthlyAmountInCents ?? 0) === 0;
}

export function getActivationPlanPerUserAmount(
  pricingDetails: StripeActivationPlanPricingDetails | null | undefined,
  currencyCode = 'USD',
  freeLabel = 'Free'
) {
  if (!pricingDetails) {
    return null;
  }

  if (isFreeActivationPlan(pricingDetails)) {
    return freeLabel;
  }

  return formatActivationPlanAmount(pricingDetails.additionalUserAmountInCents, currencyCode);
}

export function getActivationPlanMinimumSummary(
  pricingDetails: StripeActivationPlanPricingDetails | null | undefined,
  currencyCode = 'USD'
) {
  if (!pricingDetails?.includedUsers) {
    return null;
  }

  const minimumAmount = formatActivationPlanAmount(pricingDetails.minimumMonthlyAmountInCents, currencyCode);

  if (!minimumAmount) {
    return `${pricingDetails.includedUsers} user minimum`;
  }

  return `${pricingDetails.includedUsers} user minimum (${minimumAmount})`;
}
